import CheckIcon from '@mui/icons-material/Check';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, Card, CardActions, CardContent, Container, Divider, Grid, List, ListItem, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useInView } from 'react-intersection-observer';

export default function PricingSection() {
    const theme = useTheme();
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const pricingPlans = [
        {
            title: 'Mini',
            price: '2.499',
            period: 'aylık',
            description: 'Küçük mağazalar için ideal',
            features: [
                '300 Soru',
                '1 Mağaza bağlama',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Talep açarak destek',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'contained',
            popular: false,
            isSpecial: true,
            whatsappLink: 'https://wa.me/905315404669?text=Merhaba%2C%20Helpigo%20hakkında%20bilgi%20almak%20ve%20kurulum%20hakkında%20yardım%20almak%20adına%20yazıyorum.'
        },
        {
            title: 'Başlangıç',
            price: '6.999',
            period: 'aylık',
            description: 'Küçük ve orta ölçekli mağazalar için ideal',
            features: [
                '1.500 Soru',
                '1 Mağaza bağlama',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Talep açarak destek',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'outlined',
            popular: false,
            isSpecial: false
        },
        {
            title: 'Temel',
            price: '12.999',
            period: 'aylık',
            description: 'Orta ve gelişime açık ölçekli mağazalar için ideal',
            features: [
                '3.000 Soru',
                '1 Mağaza bağlama',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                '7/24 Destek',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'outlined',
            popular: false,
            isSpecial: false
        },
        {
            title: 'Profesyonel',
            price: '27.999',
            period: 'aylık',
            description: 'Markalar için ideal',
            features: [
                '8.000 Soru',
                'Birden fazla mağaza ekleme',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Öncelikli 7/24 destek',
                'Gelişmiş analitik',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'contained',
            popular: true,
            isSpecial: false
        },
        {
            title: 'Elit',
            price: '43.999',
            period: 'aylık',
            description: 'Büyük markalar için ideal',
            features: [
                '15.000 Soru',
                'Birden fazla mağaza ekleme',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Vip 7/24 destek',
                'Gelişmiş analitik',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'outlined',
            popular: false,
            isSpecial: false
        },
        {
            title: 'Diamond',
            price: '69.999',
            period: 'aylık',
            description: 'Premium markalar için ideal',
            features: [
                '30.000 Soru',
                'Birden fazla mağaza ekleme',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Vip 7/24 destek',
                'Gelişmiş analitik',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'outlined',
            popular: false,
            isSpecial: false
        }
    ];

    return (
        <Box
            id="pricing"
            sx={{
                py: { xs: 10, md: 14 },
                background: 'white',
                borderTop: `1px solid ${theme.palette.grey[100]}`,
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                    >
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                fontSize: { xs: '2rem', md: '2.75rem' },
                                fontWeight: 700,
                                mb: 2,
                                color: theme.palette.text.primary,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Fiyatlandırma Planları
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.125rem' },
                                color: theme.palette.text.secondary,
                                maxWidth: '700px',
                                mx: 'auto',
                                mb: 4,
                                fontWeight: 400,
                            }}
                        >
                            İhtiyaçlarınıza uygun fiyatlandırma planlarımızla Helpigo'yu hemen kullanmaya başlayın.
                        </Typography>
                    </motion.div>
                </Box>

                <Grid container spacing={4} ref={ref}>
                    {pricingPlans.slice(0, 3).map((plan, index) => (
                        <Grid
                            item
                            xs={12}
                            md={4}
                            key={index}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                whileHover={plan.isSpecial ? { scale: 1.02, rotate: [0, -1, 1, -1, 0] } : { scale: 1.02 }}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: '12px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        overflow: 'visible',
                                        border: plan.popular ? `2px solid ${theme.palette.accent?.main || '#635BFF'}` :
                                            plan.isSpecial ? `2px solid ${theme.palette.accent?.main || '#635BFF'}` : `1px solid ${theme.palette.grey[100]}`,
                                        transition: 'all 0.2s ease',
                                        background: 'white',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                                            borderColor: plan.popular || plan.isSpecial ? theme.palette.accent?.main || '#635BFF' : theme.palette.grey[200],
                                        }
                                    }}
                                >
                                    {plan.popular && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -12,
                                                right: 20,
                                                backgroundColor: theme.palette.accent?.main || '#635BFF',
                                                color: 'white',
                                                py: 0.5,
                                                px: 2,
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontSize: '0.8125rem',
                                                zIndex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <StarIcon fontSize="small" />
                                            Popüler
                                        </Box>
                                    )}
                                    {plan.isSpecial && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -12,
                                                right: 20,
                                                backgroundColor: theme.palette.accent?.main || '#635BFF',
                                                color: 'white',
                                                py: 0.5,
                                                px: 2,
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontSize: '0.8125rem',
                                                zIndex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <LocalOfferIcon fontSize="small" />
                                            Özel
                                        </Box>
                                    )}
                                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                                        <Typography variant="h5" component="h3" sx={{
                                            fontWeight: 600,
                                            mb: 1,
                                            color: theme.palette.text.primary,
                                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                                        }}>
                                            {plan.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
                                            {plan.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3, flexWrap: 'wrap' }}>
                                            <Typography variant="h3" component="span" sx={{
                                                fontWeight: 700,
                                                color: theme.palette.text.primary,
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                fontSize: { xs: '2rem', md: '2.5rem' },
                                            }}>
                                                {plan.price}
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: { xs: '1rem', md: '1.25rem' },
                                                        ml: 0.5,
                                                        color: theme.palette.text.secondary,
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    ₺
                                                </Typography>
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                                    ml: 1,
                                                    color: theme.palette.text.secondary,
                                                    fontWeight: 400
                                                }}
                                            >
                                                /{plan.period}
                                            </Typography>
                                            {plan.price !== '0' && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        ml: 1,
                                                        color: 'text.secondary',
                                                        alignSelf: 'flex-end',
                                                        mb: 1
                                                    }}
                                                >
                                                    (KDV Dahil)
                                                </Typography>
                                            )}
                                        </Box>
                                        <Divider sx={{ my: 3, borderColor: theme.palette.grey[100] }} />
                                        <List disablePadding>
                                            {plan.features.map((feature, idx) => (
                                                <ListItem key={idx} disablePadding sx={{ py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                                        <CheckIcon sx={{
                                                            color: theme.palette.accent?.main || '#635BFF',
                                                            fontSize: '1.25rem',
                                                        }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={feature}
                                                        primaryTypographyProps={{
                                                            sx: {
                                                                color: theme.palette.text.secondary,
                                                                fontSize: { xs: '0.875rem', md: '0.9375rem' },
                                                                fontWeight: 400,
                                                            }
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                    <CardActions sx={{ p: 4, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant={plan.popular || plan.isSpecial ? "contained" : "outlined"}
                                            size="large"
                                            component="a"
                                            href={plan.whatsappLink || "/register"}
                                            target={plan.whatsappLink ? "_blank" : "_self"}
                                            sx={{
                                                py: 1.75,
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                textDecoration: 'none',
                                                fontSize: { xs: '0.9375rem', md: '1rem' },
                                                ...(plan.popular || plan.isSpecial ? {
                                                    backgroundColor: theme.palette.accent?.main || '#635BFF',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.accent?.dark || '#4F46E5',
                                                    },
                                                } : {
                                                    borderColor: theme.palette.text.primary,
                                                    borderWidth: '1.5px',
                                                    color: theme.palette.text.primary,
                                                    '&:hover': {
                                                        borderColor: theme.palette.text.primary,
                                                        backgroundColor: theme.palette.grey[50],
                                                    },
                                                }),
                                            }}
                                        >
                                            {plan.buttonText}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={4} sx={{ mt: 4 }}>
                    {pricingPlans.slice(3).map((plan, index) => (
                        <Grid
                            item
                            xs={12}
                            md={4}
                            key={index + 3}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                                transition={{ duration: 0.5, delay: (index + 3) * 0.1 }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: '12px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        overflow: 'visible',
                                        border: plan.popular ? `2px solid ${theme.palette.accent?.main || '#635BFF'}` : `1px solid ${theme.palette.grey[100]}`,
                                        transition: 'all 0.2s ease',
                                        background: 'white',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                                            borderColor: plan.popular ? theme.palette.accent?.main || '#635BFF' : theme.palette.grey[200],
                                        }
                                    }}
                                >
                                    {plan.popular && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -12,
                                                right: 20,
                                                backgroundColor: theme.palette.accent?.main || '#635BFF',
                                                color: 'white',
                                                py: 0.5,
                                                px: 2,
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontSize: '0.8125rem',
                                                zIndex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <StarIcon fontSize="small" />
                                            Popüler
                                        </Box>
                                    )}
                                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                                        <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                                            {plan.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
                                            {plan.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3, flexWrap: 'wrap' }}>
                                            <Typography variant="h3" component="span" sx={{
                                                fontWeight: 700,
                                                color: theme.palette.text.primary,
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                fontSize: { xs: '2rem', md: '2.5rem' },
                                            }}>
                                                {plan.price}
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: { xs: '1rem', md: '1.25rem' },
                                                        ml: 0.5,
                                                        color: theme.palette.text.secondary,
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    ₺
                                                </Typography>
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                                    ml: 1,
                                                    color: theme.palette.text.secondary,
                                                    fontWeight: 400
                                                }}
                                            >
                                                /{plan.period}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ my: 3, borderColor: theme.palette.grey[100] }} />
                                        <List disablePadding>
                                            {plan.features.map((feature, idx) => (
                                                <ListItem key={idx} disablePadding sx={{ py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                                        <CheckIcon sx={{ color: theme.palette.accent?.main || '#635BFF', fontSize: '1.25rem' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={feature}
                                                        primaryTypographyProps={{
                                                            sx: {
                                                                color: theme.palette.text.secondary,
                                                                fontSize: { xs: '0.875rem', md: '0.9375rem' },
                                                                fontWeight: 400,
                                                            }
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                    <CardActions sx={{ p: 4, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant={plan.popular ? "contained" : "outlined"}
                                            size="large"
                                            component="a"
                                            href={plan.whatsappLink || "/register"}
                                            target={plan.whatsappLink ? "_blank" : "_self"}
                                            sx={{
                                                py: 1.75,
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                textDecoration: 'none',
                                                fontSize: { xs: '0.9375rem', md: '1rem' },
                                                ...(plan.popular ? {
                                                    backgroundColor: theme.palette.accent?.main || '#635BFF',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.accent?.dark || '#4F46E5',
                                                    },
                                                } : {
                                                    borderColor: theme.palette.text.primary,
                                                    borderWidth: '1.5px',
                                                    color: theme.palette.text.primary,
                                                    '&:hover': {
                                                        borderColor: theme.palette.text.primary,
                                                        backgroundColor: theme.palette.grey[50],
                                                    },
                                                }),
                                            }}
                                        >
                                            {plan.buttonText}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                <Box
                    sx={{
                        mt: { xs: 6, md: 8 },
                        p: { xs: 4, md: 5 },
                        borderRadius: '12px',
                        backgroundColor: theme.palette.grey[50],
                        border: `1px solid ${theme.palette.grey[100]}`,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h4" component="h3" sx={{ mb: 2, fontWeight: 700, color: theme.palette.text.primary }}>
                        Kurumsal Çözümler
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary }}>
                        Sınırsız soru-cevap, özel API erişimi ve size özel çözümler için kurumsal teklifimizi inceleyin.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        component="a"
                        href="https://api.whatsapp.com/send/?phone=905315404669&text=Merhaba%2C+Helpigo+hakkında+bilgi+almak+ve+kurulum+hakkında+yardım+almak+adına+yazıyorum.&type=phone_number&app_absent=0"
                        target="_blank"
                        sx={{
                            backgroundColor: theme.palette.accent?.main || '#635BFF',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: theme.palette.accent?.dark || '#4F46E5',
                            },
                            py: 1.75,
                            px: 4,
                            borderRadius: '8px',
                            fontWeight: 600,
                        }}
                        startIcon={<WhatsAppIcon />}
                    >
                        Detaylı Bilgi Al
                    </Button>
                </Box>
            </Container>
        </Box>
    );
} 